import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Link,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Tag,
  Textarea,
  Text,
  Tooltip,
  UnorderedList,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import styled from '@emotion/styled';
import { ItemBorderBox } from '@resistapp/client/components/containers/item-border-box';
import { PlusIcon } from '@resistapp/client/components/icons/plus-icon';
import { theme } from '@resistapp/client/components/shared/theme';
import {
  useAdminOrganisationAccessMetadata,
  useAdminOrganisationShipments,
  useAdminStartImpersonation,
  useGetUser,
  useOrganisation,
  useOrganisationMembers,
  useOrganisationLabWorkbooks,
  useOrganisations,
  usePatchOrganisation,
  usePostOrganisationInvites,
  usePostOrganisationUserByEmail,
} from '@resistapp/client/hooks/api';
import { useNavigateWithQuery } from '@resistapp/client/hooks/use-navigate-with-query';
import { useToastWithoutDebounce } from '@resistapp/client/hooks/use-resistomap-toast';
import { isResistomapAdminUser } from '@resistapp/client/utils/access';
import { markAuthChangingNavigation } from '@resistapp/client/utils/auth-changing-navigation';
import { formatWorkbookTimestamp, getLabWorkbookStatusColor } from '@resistapp/client/utils/lab-workbook';
import { LabWorkbookAdminModal } from '@resistapp/client/views/lab-view/components/lab-workbook-admin-modal';
import type {
  AdminOrganisationAccessMetadataResponse,
  AdminOrganisationLabWorkbookSummary,
  OrganisationInviteDeliveryStatus,
  OrganisationInviteSendResult,
} from '@resistapp/common/api-types';
import { Feature, LabFeature, allLabFeatures, resistomapOrganisationId } from '@resistapp/common/features';
import {
  AccessObjectType,
  MetricMode,
  Organisation,
  OrganisationSummary,
  OrganisationType,
  OrganisationUserRole,
  Project,
  ShipmentStatus,
  User,
} from '@resistapp/common/types';
import { useQueryClient } from '@tanstack/react-query';
import { Dictionary, clone, compact, uniq, uniqBy } from 'lodash';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useUser } from '../../contexts/use-user-context';
import {
  DeleteFeatureLink,
  DeleteLabFeatureLink,
  DeleteOrgAccessIcon,
  DeleteOrgInviteIcon,
  DeleteOrgUserIcon,
  DeleteOrganisationLink,
} from './delete-icons';

interface OrganisationRowProps {
  org: OrganisationSummary;
  projectNamesById: Dictionary<Project | null>;
}

const ACCESS_COLLAPSE_THRESHOLD = 10;

export function OrganisationRow(props: OrganisationRowProps) {
  const { org, projectNamesById } = props;

  return (
    <AccordionItem key={org.id}>
      {({ isExpanded }) => (
        <>
          <h2>
            <AccordionButton style={{ backgroundColor: theme.colors.neutral100 }}>
              <Box as="span" flex="1" textAlign="left">
                {org.name} ({org.id})
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pt={0} pb={30}>
            <OrganisationRowDetails
              organisationId={org.id}
              projectNamesById={projectNamesById}
              isExpanded={isExpanded}
            />
          </AccordionPanel>
        </>
      )}
    </AccordionItem>
  );
}

function OrganisationRowDetails({
  organisationId,
  projectNamesById,
  isExpanded,
}: {
  organisationId: number;
  projectNamesById: Dictionary<Project | null>;
  isExpanded: boolean;
}) {
  const {
    data: organisation,
    error,
    isLoading,
    isFetching,
    refetch,
  } = useOrganisation(organisationId, { enabled: isExpanded });

  if (!isExpanded) {
    return null;
  }
  if (error) {
    return (
      <HStack pt={4} spacing={3}>
        <Text color="red.700">Unable to load organisation details.</Text>
        <Button size="sm" variant="outline" isLoading={isFetching} onClick={() => void refetch()}>
          Retry
        </Button>
      </HStack>
    );
  }
  if (isLoading || !organisation) {
    return (
      <HStack pt={4} spacing={2}>
        <Spinner size="sm" />
        <Text color={theme.colors.neutral600}>Loading organisation details...</Text>
      </HStack>
    );
  }
  return <OrganisationDetails org={organisation} projectNamesById={projectNamesById} isExpanded={isExpanded} />;
}

export function OrganisationDetails(props: {
  org: Organisation;
  projectNamesById: Dictionary<Project | null>;
  isExpanded: boolean;
}) {
  const { org, projectNamesById, isExpanded } = props;
  const navigate = useNavigateWithQuery();
  const { user, setLoggedIn } = useUser();
  const showToast = useToastWithoutDebounce();
  const queryClient = useQueryClient();
  const { send: fetchCurrentUser } = useGetUser();
  const startImpersonationMutation = useAdminStartImpersonation();
  const linkChildMutation = usePatchOrganisation();
  const linkParentMutation = usePatchOrganisation();
  const unlinkChildMutation = usePatchOrganisation();
  const { data: organisations } = useOrganisations();
  const {
    isOpen: isHierarchyModalOpen,
    onOpen: onOpenHierarchyModal,
    onClose: onCloseHierarchyModal,
  } = useDisclosure();
  const [hierarchyModalTarget, setHierarchyModalTarget] = useState<'child' | 'parent'>('child');
  const [hierarchySelection, setHierarchySelection] = useState<'new' | number | null>(null);
  const [hierarchySelectionError, setHierarchySelectionError] = useState<string | null>(null);
  const [unlinkingChildOrganisationId, setUnlinkingChildOrganisationId] = useState<number | null>(null);
  const samplingPointCount = org.samplingPointAccesses.length;
  const sampleCount = org.sampleAccesses.length;

  const initialLoad = useMemo(
    () =>
      (samplingPointCount > 0 && samplingPointCount <= ACCESS_COLLAPSE_THRESHOLD) ||
      (sampleCount > 0 && sampleCount <= ACCESS_COLLAPSE_THRESHOLD),
    [sampleCount, samplingPointCount],
  );

  const [loadAccessMetadata, setLoadAccessMetadata] = useState(initialLoad);
  const previousOrgIdRef = useRef(org.id);

  useEffect(() => {
    if (previousOrgIdRef.current !== org.id) {
      previousOrgIdRef.current = org.id;
      setLoadAccessMetadata(initialLoad);
    }
  }, [org.id, initialLoad]);

  useEffect(() => {
    if (initialLoad) {
      setLoadAccessMetadata(true);
    }
  }, [initialLoad]);

  const ensureAccessMetadata = useCallback(() => {
    setLoadAccessMetadata(true);
  }, []);

  const {
    data: accessMetadata,
    isLoading: isAccessMetadataLoading,
    error: accessMetadataError,
  } = useAdminOrganisationAccessMetadata(org.id, { enabled: loadAccessMetadata });

  if (accessMetadataError) {
    throw accessMetadataError instanceof Error ? accessMetadataError : new Error(String(accessMetadataError));
  }

  const providerAdminOrgIds = (user?.organisations ?? [])
    .filter(
      membership =>
        membership.type === OrganisationType.PROVIDER && membership.membershipRole === OrganisationUserRole.ADMIN,
    )
    .map(membership => membership.id);

  const canManageOrganisation = useMemo(() => {
    if (isResistomapAdminUser(user)) {
      return true;
    }
    if (!organisations || providerAdminOrgIds.length === 0) {
      return false;
    }

    const organisationById = new Map(organisations.map(organisation => [organisation.id, organisation] as const));
    const visited = new Set<number>();
    let currentOrganisationId: number | null = org.id;

    while (currentOrganisationId != null && !visited.has(currentOrganisationId)) {
      visited.add(currentOrganisationId);
      if (providerAdminOrgIds.includes(currentOrganisationId)) {
        return true;
      }
      currentOrganisationId = organisationById.get(currentOrganisationId)?.parentId ?? null;
    }

    return false;
  }, [org.id, organisations, providerAdminOrgIds, user]);

  const canAddChild =
    isResistomapAdminUser(user) ||
    org.membershipRole === OrganisationUserRole.ADMIN ||
    providerAdminOrgIds.includes(org.id) ||
    (org.parentId != null && providerAdminOrgIds.includes(org.parentId));
  const canAddParent = canAddChild && org.parentId == null && org.id !== resistomapOrganisationId;
  const isLinkingExistingOrganisation =
    typeof hierarchySelection === 'number' &&
    (hierarchyModalTarget === 'child' ? linkChildMutation.isPending : linkParentMutation.isPending);

  const hierarchyEligibleOrganisations = useMemo(() => {
    if (!organisations) {
      return [] as OrganisationSummary[];
    }
    return organisations.filter(
      organisation => organisation.parentId == null || organisation.parentId === resistomapOrganisationId,
    );
  }, [organisations]);

  const selectableChildOrganisations = useMemo(() => {
    return hierarchyEligibleOrganisations
      .filter(organisation => organisation.id !== org.id && organisation.id !== resistomapOrganisationId)
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [hierarchyEligibleOrganisations, org.id]);

  const descendantOrganisationIds = useMemo(() => {
    if (!organisations) {
      return new Set<number>();
    }
    const childrenByParentId = new Map<number, OrganisationSummary[]>();
    organisations.forEach(organisation => {
      if (organisation.parentId == null) {
        return;
      }
      const siblings = childrenByParentId.get(organisation.parentId) ?? [];
      siblings.push(organisation);
      childrenByParentId.set(organisation.parentId, siblings);
    });
    const result = new Set<number>();
    const queue = [...(childrenByParentId.get(org.id) ?? [])];
    while (queue.length > 0) {
      const current = queue.shift() as OrganisationSummary;
      if (result.has(current.id)) {
        continue;
      }
      result.add(current.id);
      const children = childrenByParentId.get(current.id);
      if (children && children.length > 0) {
        queue.push(...children);
      }
    }
    return result;
  }, [org.id, organisations]);

  const selectableParentOrganisations = useMemo(() => {
    return hierarchyEligibleOrganisations
      .filter(organisation => organisation.id !== org.id && !descendantOrganisationIds.has(organisation.id))
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [descendantOrganisationIds, hierarchyEligibleOrganisations, org.id]);

  const parentOrganisation = useMemo(() => {
    if (!organisations || org.parentId == null) {
      return null;
    }
    return organisations.find(organisation => organisation.id === org.parentId) ?? null;
  }, [org.parentId, organisations]);

  const childOrganisations = useMemo(() => {
    if (!organisations) {
      return null;
    }
    return organisations
      .filter(organisation => organisation.parentId === org.id)
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [org.id, organisations]);

  const canImpersonateFromOrganisation = isResistomapAdminUser(user);
  const {
    data: organisationMembersForImpersonation,
    isLoading: isLoadingOrganisationMembersForImpersonation,
    error: organisationMembersForImpersonationError,
  } = useOrganisationMembers(org.id, {
    enabled: isExpanded && canImpersonateFromOrganisation,
    retry: false,
  });

  if (organisationMembersForImpersonationError) {
    throw organisationMembersForImpersonationError;
  }

  const organisationUsersForImpersonation = useMemo(
    () =>
      uniqBy(organisationMembersForImpersonation?.users ?? [], candidate => candidate.id).sort((left, right) =>
        `${left.firstName} ${left.lastName} ${left.email}`
          .toLowerCase()
          .localeCompare(`${right.firstName} ${right.lastName} ${right.email}`.toLowerCase()),
      ),
    [organisationMembersForImpersonation?.users],
  );
  const [selectedImpersonationUserId, setSelectedImpersonationUserId] = useState<number | null>(null);

  useEffect(() => {
    setSelectedImpersonationUserId(null);
  }, [org.id]);

  const refreshCurrentUserStable = useCallback(async () => {
    const response = await fetchCurrentUser();
    const isSuccessfulStatus = typeof response.status === 'number' && response.status >= 200 && response.status <= 204;
    if (!isSuccessfulStatus || !response.data) {
      throw new Error('Failed to refresh user after impersonation change.');
    }
    setLoggedIn(response.data as User);
  }, [fetchCurrentUser, setLoggedIn]);

  const handleStartImpersonationStable = useCallback(
    async (
      payload:
        | { targetType: 'EXISTING_USER'; organisationId: number; userId: number }
        | {
            targetType: 'PSEUDO_USER';
            organisationId: number;
            role: OrganisationUserRole;
          },
    ) => {
      await startImpersonationMutation.mutateAsync(payload);
      try {
        if (typeof window !== 'undefined') {
          // Boot the app cleanly as the impersonated user to avoid stale admin
          // queries triggering unauthorized->login redirects during identity switch.
          markAuthChangingNavigation();
          window.location.assign('/world');
          return;
        }

        await queryClient.cancelQueries();
        queryClient.clear();
        await refreshCurrentUserStable();
        navigate('/world', false);
      } catch (error) {
        console.warn('Failed to refresh user after starting impersonation, forcing reload.', error);
        if (typeof window !== 'undefined') {
          markAuthChangingNavigation();
          window.location.assign('/world');
          return;
        }
        throw error;
      }
    },
    [navigate, queryClient, refreshCurrentUserStable, startImpersonationMutation],
  );

  const handleStartPseudoUserImpersonationStable = useCallback(
    async (role: OrganisationUserRole) => {
      try {
        await handleStartImpersonationStable({
          targetType: 'PSEUDO_USER',
          organisationId: org.id,
          role,
        });
      } catch (error) {
        showToast({
          title: 'Failed to start impersonation',
          description: error instanceof Error ? error.message : 'Please try again.',
          status: 'error',
          duration: 7000,
        });
      }
    },
    [handleStartImpersonationStable, org.id, showToast],
  );

  const handleStartExistingUserImpersonationStable = useCallback(async () => {
    if (!selectedImpersonationUserId) {
      showToast({
        title: 'Select a user',
        description: 'Choose an existing organisation user first.',
        status: 'warning',
        duration: 5000,
      });
      return;
    }

    try {
      await handleStartImpersonationStable({
        targetType: 'EXISTING_USER',
        organisationId: org.id,
        userId: selectedImpersonationUserId,
      });
    } catch (error) {
      showToast({
        title: 'Failed to start impersonation',
        description: error instanceof Error ? error.message : 'Please try again.',
        status: 'error',
        duration: 7000,
      });
    }
  }, [handleStartImpersonationStable, org.id, selectedImpersonationUserId, showToast]);

  const handleOpenHierarchyModal = useCallback(
    (target: 'child' | 'parent') => {
      setHierarchyModalTarget(target);
      setHierarchySelection(null);
      setHierarchySelectionError(null);
      onOpenHierarchyModal();
    },
    [onOpenHierarchyModal],
  );

  const handleCloseHierarchyModal = useCallback(() => {
    setHierarchySelection(null);
    setHierarchySelectionError(null);
    onCloseHierarchyModal();
  }, [onCloseHierarchyModal]);

  const handleHierarchySelectionChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value;
    if (selectedValue === '') {
      setHierarchySelection(null);
      setHierarchySelectionError(null);
      return;
    }

    if (selectedValue === 'new') {
      setHierarchySelection('new');
      setHierarchySelectionError(null);
      return;
    }

    const nextId = Number(selectedValue);
    setHierarchySelection(Number.isFinite(nextId) ? nextId : null);
    setHierarchySelectionError(null);
  }, []);

  const handleConfirmHierarchyLink = useCallback(async () => {
    if (hierarchySelection === 'new') {
      handleCloseHierarchyModal();
      if (hierarchyModalTarget === 'child') {
        navigate(`/organisation/create?parentId=${org.id}`, false);
      } else {
        navigate(`/organisation/create?childId=${org.id}`, false);
      }
      return;
    }

    if (typeof hierarchySelection !== 'number') {
      setHierarchySelectionError('Please select an organisation to link.');
      return;
    }

    if (hierarchyModalTarget === 'child') {
      try {
        await linkChildMutation.mutateAsync({ id: hierarchySelection, parentId: org.id });
        await queryClient.invalidateQueries({ queryKey: ['/organisations'] });
        showToast({
          title: 'Organisation linked',
          description: 'The existing organisation has been linked as a child.',
          status: 'success',
          duration: 7000,
        });
        handleCloseHierarchyModal();
      } catch (error) {
        showToast({
          title: 'Failed to link organisation',
          description: error instanceof Error ? error.message : 'Please try again.',
          status: 'error',
          duration: 7000,
        });
      }
      return;
    }

    try {
      await linkParentMutation.mutateAsync({ id: org.id, parentId: hierarchySelection });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['/organisations'] }),
        queryClient.invalidateQueries({ queryKey: [`/organisation/${org.id}`] }),
      ]);
      showToast({
        title: 'Parent organisation updated',
        description: 'The organisation parent has been updated successfully.',
        status: 'success',
        duration: 7000,
      });
      handleCloseHierarchyModal();
    } catch (error) {
      showToast({
        title: 'Failed to update parent',
        description: error instanceof Error ? error.message : 'Please try again.',
        status: 'error',
        duration: 7000,
      });
    }
  }, [
    handleCloseHierarchyModal,
    hierarchyModalTarget,
    hierarchySelection,
    linkChildMutation,
    linkParentMutation,
    navigate,
    org.id,
    queryClient,
    showToast,
  ]);

  const handleUnlinkChild = useCallback(
    async (child: OrganisationSummary) => {
      const confirmed = window.confirm(
        `Removing ${child.name} (#${child.id}) as a child will make all of its organisations lose access and features inherited from ${org.name}. Continue?`,
      );
      if (!confirmed) {
        return;
      }
      setUnlinkingChildOrganisationId(child.id);
      try {
        await unlinkChildMutation.mutateAsync({ id: child.id, parentId: null });
        await queryClient.invalidateQueries({ queryKey: ['/organisations'] });
        showToast({
          title: 'Child organisation removed',
          description: `${child.name} is no longer a child of ${org.name}.`,
          status: 'success',
          duration: 7000,
        });
      } catch (error) {
        showToast({
          title: 'Failed to remove child',
          description: error instanceof Error ? error.message : 'Please try again.',
          status: 'error',
          duration: 7000,
        });
      } finally {
        setUnlinkingChildOrganisationId(null);
      }
    },
    [org.name, queryClient, showToast, unlinkChildMutation],
  );

  return (
    <>
      <OrganisationDemo org={org} />
      {canImpersonateFromOrganisation ? (
        <>
          <FieldTitle>Impersonate:</FieldTitle>
          <VStack align="stretch" spacing={3}>
            <HStack spacing={3} flexWrap="wrap">
              <Button
                size="sm"
                colorScheme="orange"
                onClick={() => {
                  void handleStartPseudoUserImpersonationStable(OrganisationUserRole.MEMBER);
                }}
                isLoading={startImpersonationMutation.isPending}
                isDisabled={startImpersonationMutation.isPending}
              >
                View as member
              </Button>
              <Button
                size="sm"
                variant="outline"
                colorScheme="orange"
                onClick={() => {
                  void handleStartPseudoUserImpersonationStable(OrganisationUserRole.ADMIN);
                }}
                isLoading={startImpersonationMutation.isPending}
                isDisabled={startImpersonationMutation.isPending}
              >
                View as org admin
              </Button>
            </HStack>
            {isLoadingOrganisationMembersForImpersonation ? (
              <Text color={theme.colors.neutral600}>Loading existing users...</Text>
            ) : organisationUsersForImpersonation.length > 0 ? (
              <HStack spacing={3} align="center" flexWrap="wrap">
                <Select
                  placeholder="Select existing user"
                  maxW="420px"
                  value={selectedImpersonationUserId ?? ''}
                  onChange={event => {
                    const nextValue = Number(event.target.value);
                    setSelectedImpersonationUserId(Number.isInteger(nextValue) && nextValue > 0 ? nextValue : null);
                  }}
                >
                  {organisationUsersForImpersonation.map(candidate => (
                    <option key={candidate.id} value={candidate.id}>
                      {candidate.firstName} {candidate.lastName} ({candidate.email})
                    </option>
                  ))}
                </Select>
                <Button
                  size="sm"
                  onClick={() => {
                    void handleStartExistingUserImpersonationStable();
                  }}
                  isLoading={startImpersonationMutation.isPending}
                  isDisabled={startImpersonationMutation.isPending || !selectedImpersonationUserId}
                >
                  View as existing user
                </Button>
              </HStack>
            ) : (
              <Text color={theme.colors.neutral600}>No existing users in this organisation.</Text>
            )}
          </VStack>
        </>
      ) : null}
      <FieldTitle>Hierarchy:</FieldTitle>
      {parentOrganisation ? (
        <HStack fontWeight={600} spacing={2} align="center">
          <Link as={RouterLink} to={`/admin/organisation/${parentOrganisation.id}`} color={theme.colors.blue800}>
            {parentOrganisation.name}
          </Link>
          <Text fontWeight={500} color={theme.colors.neutral600}>
            (parent)
          </Text>
        </HStack>
      ) : org.parentId != null ? (
        <HStack fontWeight={600} spacing={2} align="center">
          <Link as={RouterLink} to={`/admin/organisation/${org.parentId}`} color={theme.colors.blue800}>
            Organisation #{org.parentId}
          </Link>
          <Text fontWeight={500} color={theme.colors.neutral600}>
            (parent)
          </Text>
        </HStack>
      ) : canAddParent ? (
        <Button
          size="sm"
          variant="outline"
          colorScheme="blue"
          onClick={() => {
            handleOpenHierarchyModal('parent');
          }}
        >
          Add parent organisation
        </Button>
      ) : (
        <Text color={theme.colors.neutral600}>No parent organisation</Text>
      )}
      <UnorderedList spacing={2} ml="20px">
        <ListItem>
          <Box>
            <Text fontWeight={700}>{org.name}</Text>
            <UnorderedList spacing={1} ml="20px">
              {childOrganisations === null ? (
                <ListItem>
                  <HStack spacing={2}>
                    <Spinner size="xs" />
                    <Text fontSize="sm" color={theme.colors.neutral600}>
                      Loading child organisations…
                    </Text>
                  </HStack>
                </ListItem>
              ) : childOrganisations.length === 0 && !canAddChild ? (
                <ListItem color={theme.colors.neutral600}>No child organisations</ListItem>
              ) : (
                childOrganisations.map(child => (
                  <ListItem key={child.id}>
                    <HStack spacing={2} align="center">
                      <Link as={RouterLink} to={`/admin/organisation/${child.id}`} color={theme.colors.blue800}>
                        {child.name} ({child.id})
                      </Link>
                      {canAddChild ? (
                        <Button
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => {
                            void handleUnlinkChild(child);
                          }}
                          isLoading={unlinkingChildOrganisationId === child.id && unlinkChildMutation.isPending}
                          isDisabled={unlinkingChildOrganisationId === child.id && unlinkChildMutation.isPending}
                        >
                          x
                        </Button>
                      ) : null}
                    </HStack>
                  </ListItem>
                ))
              )}
              {canAddChild ? (
                <ListItem>
                  <Button
                    size="xs"
                    colorScheme="blue"
                    onClick={() => {
                      handleOpenHierarchyModal('child');
                    }}
                  >
                    Add child organisation
                  </Button>
                </ListItem>
              ) : null}
            </UnorderedList>
          </Box>
        </ListItem>
      </UnorderedList>
      <Modal isOpen={isHierarchyModalOpen} onClose={handleCloseHierarchyModal} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {hierarchyModalTarget === 'child' ? 'Add child organisation' : 'Add parent organisation'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <FormControl isInvalid={Boolean(hierarchySelectionError)}>
                <FormLabel>
                  {hierarchyModalTarget === 'child'
                    ? 'How would you like to add a child organisation?'
                    : 'How would you like to set the parent organisation?'}
                </FormLabel>
                <Select
                  placeholder="Select option"
                  value={hierarchySelection ?? ''}
                  onChange={handleHierarchySelectionChange}
                >
                  <option value="new">Create a new organisation</option>
                  {(hierarchyModalTarget === 'child'
                    ? selectableChildOrganisations
                    : selectableParentOrganisations
                  ).map(organisation => (
                    <option key={organisation.id} value={organisation.id}>
                      {organisation.name} ({organisation.id})
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{hierarchySelectionError}</FormErrorMessage>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCloseHierarchyModal}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => {
                void handleConfirmHierarchyLink();
              }}
              isLoading={isLinkingExistingOrganisation}
              isDisabled={isLinkingExistingOrganisation}
            >
              Continue
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <OrganisationFeatures org={org} />
      <OrganisationLabFeatures org={org} />
      <OrganisationDefaultMetric org={org} />
      <OrganisationUsers org={org} isExpanded={isExpanded} />
      <OrganisationProjectAccess org={org} projectNamesById={projectNamesById} />
      <OrganisationSamplingPointAccess
        org={org}
        metadata={accessMetadata}
        isMetadataLoading={isAccessMetadataLoading}
        ensureMetadata={ensureAccessMetadata}
      />
      <OrganisationSampleAccess
        org={org}
        metadata={accessMetadata}
        isMetadataLoading={isAccessMetadataLoading}
        ensureMetadata={ensureAccessMetadata}
      />
      <OtherOrganisationData org={org} canManageOrganisation={canManageOrganisation} />
    </>
  );
}

function OtherOrganisationData({ org, canManageOrganisation }: { org: Organisation; canManageOrganisation: boolean }) {
  const patcher = usePatchOrganisation();
  const [newName, setNewName] = useState(org.name);
  const [newExternalName, setNewExternalName] = useState(org.externalName ?? '');

  useEffect(() => {
    setNewName(org.name);
    setNewExternalName(org.externalName ?? '');
  }, [org.externalName, org.id, org.name]);

  const normalizedExternalName = newExternalName.trim();
  const nextExternalName = normalizedExternalName.length > 0 ? normalizedExternalName : null;
  const externalNameChanged = nextExternalName !== (org.externalName ?? null);

  return (
    <>
      <FieldTitle textAlign="left">Misc admin</FieldTitle>
      <Flex>
        <Input
          width="400px"
          value={newName}
          onChange={event => {
            setNewName(event.target.value);
          }}
        />
        <Button
          disabled={!newName || newName === org.name}
          onClick={_event => {
            void (async () => {
              await patcher.mutateAsync({ id: org.id, name: newName });
              window.location.reload();
            })();
          }}
        >
          Rename
        </Button>
      </Flex>
      {canManageOrganisation ? (
        <Flex pt={4}>
          <Input
            width="400px"
            placeholder="External name (customer-facing)"
            value={newExternalName}
            onChange={event => {
              setNewExternalName(event.target.value);
            }}
          />
          <Button
            disabled={!externalNameChanged}
            onClick={_event => {
              void (async () => {
                await patcher.mutateAsync({ id: org.id, externalName: nextExternalName });
                window.location.reload();
              })();
            }}
          >
            Save external name
          </Button>
        </Flex>
      ) : null}
      <Flex pt={5}>
        Delete? <DeleteOrganisationLink organisation={org} />
      </Flex>
      <Flex />
    </>
  );
}

export function OrganisationLabWorkbooks({ org }: { org: Organisation }) {
  const { user } = useUser();
  const canAssignShippingQuota = org.type === OrganisationType.CUSTOMER && isResistomapAdminUser(user);
  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch: refetchWorkbooks,
  } = useOrganisationLabWorkbooks(org.id, { enabled: true });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const handleRefresh = useCallback(() => {
    void refetchWorkbooks();
  }, [refetchWorkbooks]);

  if (error) {
    throw error;
  }

  const workbooks = data?.workbooks ?? [];
  const showLoadingState = isLoading && workbooks.length === 0;

  return (
    <Box pt={5} pb={5}>
      <Flex align="center" justify="space-between" gap="12px" wrap="wrap">
        <FieldTitle paddingTop={0} paddingBottom={0}>
          Lab workbooks:
        </FieldTitle>
        <HStack spacing={2}>
          <Button size="sm" colorScheme="blue" onClick={onOpen}>
            Create workbook
          </Button>
          <Button size="sm" variant="ghost" onClick={handleRefresh} isLoading={isFetching}>
            Refresh
          </Button>
        </HStack>
      </Flex>
      <LabWorkbookAdminModal
        mode="create"
        organisationId={org.id}
        canAssignShippingQuota={canAssignShippingQuota}
        isOpen={isOpen}
        onClose={onClose}
        onCreated={() => {
          void refetchWorkbooks();
        }}
      />
      <Box pt={3}>
        {showLoadingState ? (
          <HStack spacing={2} align="center">
            <Spinner size="sm" />
            <Text color="gray.600">Loading workbooks...</Text>
          </HStack>
        ) : workbooks.length === 0 ? (
          <Text color="gray.600">No lab workbooks yet.</Text>
        ) : (
          <VStack align="stretch" spacing={2}>
            {workbooks.map(workbook => {
              const createdLabel = formatWorkbookTimestamp(workbook.createdAt);
              const workbookHref = `/lab/${workbook.id}`;
              const trimmedName = workbook.name ? workbook.name.trim() : '';
              const titleLabel = trimmedName || createdLabel;
              return (
                <ItemBorderBox key={workbook.id}>
                  <VStack align="stretch" spacing={3} width="100%">
                    <Flex align="center" justify="space-between" gap="12px" width="100%">
                      <Box textAlign="left" wordBreak="break-word" flex="1">
                        <Link
                          as={RouterLink}
                          to={workbookHref}
                          display="block"
                          color={theme.colors.neutral800}
                          fontWeight={600}
                          textDecoration="none"
                          _hover={{ textDecoration: 'underline', color: theme.colors.blue800 }}
                        >
                          {titleLabel}
                          <Text fontSize="sm" color={theme.colors.neutral600}>
                            Status updated {formatWorkbookTimestamp(workbook.updatedAt)}
                          </Text>
                          {workbook.projectLead ? (
                            <Text fontSize="sm" color={theme.colors.neutral600}>
                              Project lead: {workbook.projectLead}
                            </Text>
                          ) : null}
                          {workbook.genePanelName || workbook.genePanelId ? (
                            <Text fontSize="sm" color={theme.colors.neutral600}>
                              Gene panel: {workbook.genePanelName ?? `#${workbook.genePanelId}`}
                            </Text>
                          ) : null}
                        </Link>
                      </Box>
                      <Badge colorScheme={getLabWorkbookStatusColor(workbook.status)}>{workbook.status}</Badge>
                    </Flex>
                    {canAssignShippingQuota ? <OrganisationWorkbookShippingQuotaSummary workbook={workbook} /> : null}
                  </VStack>
                </ItemBorderBox>
              );
            })}
          </VStack>
        )}
      </Box>
    </Box>
  );
}

function OrganisationWorkbookShippingQuotaSummary({
  workbook,
}: {
  workbook: AdminOrganisationLabWorkbookSummary;
}) {
  return (
    <VStack align="stretch" spacing={2} pt={3} borderTop="1px solid" borderColor="gray.100">
      <HStack spacing={2} wrap="wrap">
        <Text fontSize="xs" fontWeight="semibold" color="gray.600">
          Shipping quota
        </Text>
        <Badge colorScheme={workbook.shippingRequestsAvailable > 0 ? 'green' : 'red'}>
          Available: {workbook.shippingRequestsAvailable}
        </Badge>
        <Badge colorScheme="blue">Pending: {workbook.shippingRequestsPending}</Badge>
        <Badge colorScheme="purple">Spent: {workbook.shippingRequestsUsed}</Badge>
        {workbook.xlShipmentsAuthorised ? <Badge colorScheme="green">XL authorised</Badge> : null}
      </HStack>
    </VStack>
  );
}

export function OrganisationShipments({ org }: { org: Organisation }) {
  const { user } = useUser();
  const isGlobalAdmin = isResistomapAdminUser(user);
  const [includeReceived, setIncludeReceived] = useState(false);

  const shipmentParams = useMemo(() => {
    const params = new URLSearchParams();
    if (includeReceived) {
      params.set('includeReceived', '1');
    }
    return params;
  }, [includeReceived]);

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch: refetchShipments,
  } = useAdminOrganisationShipments(org.id, shipmentParams, { enabled: isGlobalAdmin });

  if (!isGlobalAdmin) {
    return (
      <Box pt={5} pb={5}>
        <FieldTitle paddingTop={0} paddingBottom={0}>
          Shipments:
        </FieldTitle>
        <Text color="gray.600">Shipment browsing is available for Resistomap admins.</Text>
      </Box>
    );
  }

  if (error) {
    throw error;
  }

  const shipments = data?.shipments ?? [];
  const showLoadingState = isLoading && shipments.length === 0;

  return (
    <Box pt={5} pb={5}>
      <Flex align="center" justify="space-between" gap="12px" wrap="wrap">
        <FieldTitle paddingTop={0} paddingBottom={0}>
          Shipments:
        </FieldTitle>
        <HStack spacing={2}>
          <Checkbox
            isChecked={includeReceived}
            onChange={event => {
              setIncludeReceived(event.currentTarget.checked);
            }}
          >
            Show received
          </Checkbox>
          <Button size="sm" variant="ghost" onClick={() => void refetchShipments()} isLoading={isFetching}>
            Refresh
          </Button>
        </HStack>
      </Flex>
      <Box pt={3}>
        {showLoadingState ? (
          <HStack spacing={2} align="center">
            <Spinner size="sm" />
            <Text color="gray.600">Loading shipments...</Text>
          </HStack>
        ) : shipments.length === 0 ? (
          <Text color="gray.600">No shipments yet.</Text>
        ) : (
          <VStack align="stretch" spacing={2}>
            {shipments.map(shipment => {
              const statusColor =
                shipment.status === ShipmentStatus.RECEIVED
                  ? 'green'
                  : shipment.status === ShipmentStatus.CANCELLED
                    ? 'red'
                    : shipment.status === ShipmentStatus.PROCESSED
                      ? 'teal'
                      : shipment.status === ShipmentStatus.PICKED_UP
                        ? 'purple'
                        : shipment.status === ShipmentStatus.SCHEDULED
                          ? 'blue'
                          : 'orange';

              return (
                <ItemBorderBox key={shipment.id}>
                  <Flex align="center" justify="space-between" gap="12px" width="100%">
                    <Box textAlign="left" wordBreak="break-word" flex="1">
                      <Link
                        as={RouterLink}
                        to={`/admin/shipment/${shipment.id}`}
                        display="block"
                        color={theme.colors.neutral800}
                        fontWeight={600}
                        textDecoration="none"
                        _hover={{ textDecoration: 'underline', color: theme.colors.blue800 }}
                      >
                        Shipment #{shipment.id}
                        <Text fontSize="sm" color={theme.colors.neutral600}>
                          {shipment.pickupDate} ({shipment.pickupWindowStart} - {shipment.pickupWindowEnd})
                        </Text>
                        <Text fontSize="sm" color={theme.colors.neutral600}>
                          {shipment.contactName} · {shipment.contactEmail}
                        </Text>
                        {shipment.labWorkbook ? (
                          <Text fontSize="sm" color={theme.colors.neutral600}>
                            Lab workbook: {shipment.labWorkbook.name ?? 'Untitled workbook'}
                          </Text>
                        ) : null}
                        <Text fontSize="sm" color={theme.colors.neutral600}>
                          Updated {formatWorkbookTimestamp(shipment.updatedAt)}
                        </Text>
                      </Link>
                    </Box>
                    <Badge colorScheme={statusColor}>{shipment.status}</Badge>
                  </Flex>
                </ItemBorderBox>
              );
            })}
          </VStack>
        )}
      </Box>
    </Box>
  );
}

function OrganisationDemo({ org }: { org: Organisation }) {
  if (!org.isDemo) {
    return null;
  }
  return (
    <Box pt={5} pb={5}>
      <div style={{ color: theme.colors.maroon, fontStyle: 'italic' }}>This is a Demo organisation</div>
    </Box>
  );
}

function OrganisationFeatures({ org }: { org: Organisation }) {
  const patcher = usePatchOrganisation();

  return (
    <>
      <FieldTitle pt={10}>Features:</FieldTitle>
      <Flex gap={10}>
        {compact([
          <ItemBorderBox style={{ opacity: 0.5 }} key="research-static">
            Research
          </ItemBorderBox>,
          ...org.features.map((feature, _key) => (
            <ItemBorderBox key={_key}>
              {feature} <DeleteFeatureLink featureToDelete={feature} organisation={org} />
            </ItemBorderBox>
          )),
          ...Object.values(Feature)
            .filter(f => f !== Feature.ADMIN && !org.features.includes(f))
            .map(f => (
              <ItemBorderBox
                key={f}
                style={{ opacity: 0.5, cursor: 'pointer', userSelect: 'none' }}
                _hover={{ opacity: '1.0 !important' }}
                onClick={() => {
                  const updatedFeatures = [...org.features, f];
                  void (async () => {
                    await patcher.mutateAsync({ id: org.id, features: updatedFeatures });
                    window.location.reload();
                  })();
                }}
              >
                <PlusIcon />
                &nbsp;{f}
              </ItemBorderBox>
            )),
        ])}
      </Flex>
    </>
  );
}

function OrganisationLabFeatures({ org }: { org: Organisation }) {
  const { user } = useUser();
  const patcher = usePatchOrganisation();
  const labFeatures = org.labFeatures;
  const isEditable = isResistomapAdminUser(user) && org.type !== OrganisationType.CUSTOMER;

  const handleAddFeature = useCallback(
    (feature: LabFeature) => {
      const updatedFeatures = [...labFeatures, feature];
      void (async () => {
        await patcher.mutateAsync({ id: org.id, labFeatures: updatedFeatures });
        window.location.reload();
      })();
    },
    [labFeatures, org.id, patcher],
  );

  return (
    <>
      <FieldTitle pt={10}>Lab features:</FieldTitle>
      <Flex gap={10}>
        {labFeatures.length === 0 ? (
          <ItemBorderBox style={{ opacity: 0.5 }}>None</ItemBorderBox>
        ) : (
          labFeatures.map(feature => (
            <ItemBorderBox key={feature}>
              {feature.replace(/_/g, ' ')}{' '}
              {isEditable ? <DeleteLabFeatureLink featureToDelete={feature} organisation={org} /> : null}
            </ItemBorderBox>
          ))
        )}
        {isEditable
          ? allLabFeatures
              .filter(feature => !labFeatures.includes(feature))
              .map(feature => (
                <ItemBorderBox
                  key={feature}
                  style={{ opacity: 0.5, cursor: 'pointer', userSelect: 'none' }}
                  _hover={{ opacity: '1.0 !important' }}
                  onClick={() => {
                    handleAddFeature(feature);
                  }}
                >
                  <PlusIcon />
                  &nbsp;{feature.replace(/_/g, ' ')}
                </ItemBorderBox>
              ))
          : null}
      </Flex>
    </>
  );
}

function OrganisationDefaultMetric({ org }: { org: Organisation }) {
  const patcher = usePatchOrganisation();

  return (
    <>
      <FieldTitle pt={10}>Default metric:</FieldTitle>
      <Flex gap={10}>
        {compact([
          ...Object.values(MetricMode).map(metric => (
            <ItemBorderBox
              key={metric}
              style={metric === org.defaultMetric ? {} : { opacity: 0.5, cursor: 'pointer', userSelect: 'none' }}
              _hover={metric === org.defaultMetric ? {} : { opacity: '1.0 !important' }}
              onClick={
                metric === org.defaultMetric
                  ? undefined
                  : () => {
                      void (async () => {
                        await patcher.mutateAsync({ id: org.id, defaultMetric: metric });
                        window.location.reload();
                      })();
                    }
              }
            >
              {metric === org.defaultMetric ? metric : <>&nbsp;{metric}</>}
            </ItemBorderBox>
          )),
        ])}
      </Flex>
    </>
  );
}

function parseInviteEmails(rawValue: string) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const normalizedEmails = uniq(
    rawValue
      .split(/[\n,;]+/)
      .map(email => email.trim().toLowerCase())
      .filter(email => email.length > 0),
  );
  const validEmails = normalizedEmails.filter(email => emailRegex.test(email));
  const invalidEmails = normalizedEmails.filter(email => !emailRegex.test(email));
  return { validEmails, invalidEmails };
}

function formatInviteDate(isoDate: string): string {
  const parsed = new Date(isoDate);
  return Number.isNaN(parsed.getTime()) ? isoDate : parsed.toLocaleDateString();
}

function formatInviteDateTime(isoDate: string): string {
  const parsed = new Date(isoDate);
  return Number.isNaN(parsed.getTime()) ? isoDate : parsed.toLocaleString();
}

const inviteStatusMetaByStatus: Record<OrganisationInviteDeliveryStatus, { label: string; colorScheme: string }> = {
  NOT_SENT: { label: 'Not sent', colorScheme: 'orange' },
  SEND_FAILED: { label: 'Send failed', colorScheme: 'red' },
  SENT: { label: 'Accepted by SendGrid', colorScheme: 'blue' },
  PROCESSED: { label: 'Processed', colorScheme: 'cyan' },
  DELIVERED: { label: 'Delivered', colorScheme: 'green' },
  DEFERRED: { label: 'Deferred', colorScheme: 'yellow' },
  BOUNCE: { label: 'Bounced', colorScheme: 'red' },
  DROPPED: { label: 'Dropped', colorScheme: 'red' },
};

function OrganisationUsers(props: { org: Organisation; isExpanded: boolean }) {
  const { org, isExpanded } = props;
  const showToast = useToastWithoutDebounce();
  const { data: members, isLoading, error, refetch } = useOrganisationMembers(org.id, { enabled: isExpanded });
  const postInvites = usePostOrganisationInvites(org.id);
  const postOrganisationUserByEmail = usePostOrganisationUserByEmail(org.id);
  const [existingUserEmailInput, setExistingUserEmailInput] = useState('');
  const [existingUserValidationError, setExistingUserValidationError] = useState<string | null>(null);
  const [inviteInput, setInviteInput] = useState('');
  const [inviteValidationErrors, setInviteValidationErrors] = useState<string[]>([]);
  const [inviteResults, setInviteResults] = useState<OrganisationInviteSendResult[]>([]);

  if (error) {
    throw error;
  }

  const refreshMembers = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const onSendInvites = useCallback(async () => {
    const { validEmails, invalidEmails } = parseInviteEmails(inviteInput);
    if (invalidEmails.length > 0) {
      setInviteValidationErrors(invalidEmails.map(email => `${email} is not a valid email address`));
      return;
    }
    if (validEmails.length === 0) {
      setInviteValidationErrors(['Please enter at least one email address']);
      return;
    }
    setInviteValidationErrors([]);

    const response = await postInvites.mutateAsync({ emails: validEmails });
    setInviteResults(response.results);
    setInviteInput('');
    await refreshMembers();

    const sentCount = response.results.filter(result => result.sent).length;
    const failedEmails = response.results
      .filter(result => !result.sent)
      .map(result => result.email)
      .sort((left, right) => left.localeCompare(right));

    if (sentCount > 0) {
      showToast({
        title: `Sent ${sentCount} invitation${sentCount === 1 ? '' : 's'}`,
        status: 'success',
      });
    }
    if (failedEmails.length > 0) {
      showToast({
        title: 'Failed to send some invitations',
        description: failedEmails.join(', '),
        status: 'error',
      });
    }
  }, [inviteInput, postInvites, refreshMembers, showToast]);

  const onAddExistingUser = useCallback(async () => {
    const { validEmails, invalidEmails } = parseInviteEmails(existingUserEmailInput);
    if (invalidEmails.length > 0 || validEmails.length !== 1) {
      setExistingUserValidationError('Please enter exactly one valid email address');
      return;
    }

    setExistingUserValidationError(null);
    const response = await postOrganisationUserByEmail.mutateAsync({
      email: validEmails[0],
    });
    setExistingUserEmailInput('');
    await refreshMembers();
    showToast({
      title: response.added ? 'User added to organisation' : 'User already in organisation',
      description: response.email,
      status: response.added ? 'success' : 'info',
    });
  }, [existingUserEmailInput, postOrganisationUserByEmail, refreshMembers, showToast]);

  const users = members?.users ?? [];
  const invites = members?.invites ?? [];

  return (
    <>
      <FieldTitle>Users:</FieldTitle>
      <Flex wrap="wrap" gap="20px">
        {isLoading ? (
          <HStack spacing={2}>
            <Spinner size="xs" />
            <Text color={theme.colors.neutral600}>Loading users...</Text>
          </HStack>
        ) : users.length === 0 ? (
          <Text color={theme.colors.neutral600}>No users in this organisation.</Text>
        ) : (
          users.map(user => (
            <ItemBorderBox key={user.id}>
              {user.email} ({user.role.toLowerCase()}){' '}
              <DeleteOrgUserIcon refreshMembers={refreshMembers} organisation={org} user={user} />
            </ItemBorderBox>
          ))
        )}
      </Flex>

      <FieldTitle pt={10}>Add existing user:</FieldTitle>
      <FormControl isInvalid={existingUserValidationError != null}>
        <FormLabel>User email</FormLabel>
        <Input
          value={existingUserEmailInput}
          onChange={event => {
            setExistingUserEmailInput(event.target.value);
          }}
          placeholder="name@example.com"
        />
        <FormErrorMessage>{existingUserValidationError ?? ''}</FormErrorMessage>
      </FormControl>
      <Button
        mt={3}
        size="sm"
        colorScheme="blue"
        isLoading={postOrganisationUserByEmail.isPending}
        onClick={() => {
          void onAddExistingUser();
        }}
      >
        Add user
      </Button>
      {postOrganisationUserByEmail.error ? (
        <Text mt={2} color={theme.colors.red700}>
          {postOrganisationUserByEmail.error.message}
        </Text>
      ) : null}

      <FieldTitle pt={10}>Pending invites:</FieldTitle>
      <Flex wrap="wrap" gap="20px">
        {isLoading ? (
          <HStack spacing={2}>
            <Spinner size="xs" />
            <Text color={theme.colors.neutral600}>Loading invites...</Text>
          </HStack>
        ) : invites.length === 0 ? (
          <Text color={theme.colors.neutral600}>No pending invites.</Text>
        ) : (
          invites.map(invite => (
            <ItemBorderBox key={invite.id}>
              <VStack align="start" spacing={1}>
                <Text>{invite.email}</Text>
                <Text fontSize="sm" color={theme.colors.neutral600}>
                  Expires {formatInviteDate(invite.expiresAt)}
                </Text>
                <Tag
                  size="sm"
                  colorScheme={inviteStatusMetaByStatus[invite.deliveryStatus].colorScheme}
                  variant="subtle"
                >
                  {inviteStatusMetaByStatus[invite.deliveryStatus].label}
                </Tag>
                {invite.deliveryStatusAt ? (
                  <Text fontSize="sm" color={theme.colors.neutral600}>
                    Updated {formatInviteDateTime(invite.deliveryStatusAt)}
                  </Text>
                ) : null}
                {invite.deliveryStatusReason ? (
                  <Text fontSize="sm" color={theme.colors.neutral700}>
                    {invite.deliveryStatusReason}
                  </Text>
                ) : null}
              </VStack>{' '}
              <DeleteOrgInviteIcon
                refreshMembers={refreshMembers}
                organisation={org}
                inviteId={invite.id}
                inviteEmail={invite.email}
              />
            </ItemBorderBox>
          ))
        )}
      </Flex>

      <FieldTitle pt={10}>Invite users:</FieldTitle>
      <FormControl isInvalid={inviteValidationErrors.length > 0}>
        <FormLabel>Email addresses</FormLabel>
        <Textarea
          value={inviteInput}
          onChange={event => {
            setInviteInput(event.target.value);
          }}
          placeholder="Emails (comma or newline separated)"
          rows={4}
        />
        <FormErrorMessage>{inviteValidationErrors.join(', ')}</FormErrorMessage>
      </FormControl>
      <Text mt={2} fontSize="sm" color={theme.colors.neutral600}>
        Separate email addresses with commas or new lines. You&apos;ll receive a BCC copy of sent invitations.
      </Text>
      <Button
        mt={3}
        size="sm"
        colorScheme="blue"
        isLoading={postInvites.isPending}
        onClick={() => {
          void onSendInvites();
        }}
      >
        Send invitations
      </Button>
      {postInvites.error ? (
        <Text mt={2} color={theme.colors.red700}>
          {postInvites.error.message}
        </Text>
      ) : null}
      {inviteResults.length > 0 ? (
        <VStack mt={3} align="stretch" spacing={1}>
          {inviteResults.map(result => (
            <Text
              key={`${result.email}-${result.inviteId}`}
              fontSize="sm"
              color={result.sent ? 'green.700' : 'red.700'}
            >
              {inviteStatusMetaByStatus[result.deliveryStatus].label}: {result.email}
            </Text>
          ))}
        </VStack>
      ) : null}
    </>
  );
}

function OrganisationProjectAccess({
  org,
  projectNamesById,
}: {
  org: Organisation;
  projectNamesById: Dictionary<Project | null>;
}) {
  return (
    <>
      <FieldTitle>Project access:</FieldTitle>
      <SimpleGrid minChildWidth="200px" spacingX="20px" spacingY="10px">
        {org.projectAccesses.length === 0 ? (
          '-'
        ) : org.id === resistomapOrganisationId ? (
          <ItemBorderBox>All projects</ItemBorderBox>
        ) : (
          sortIds(org.projectAccesses).map(projectId => {
            const project = projectNamesById[projectId] ?? null;
            const projectLabel = project?.name ?? `Project ${projectId}`;
            return (
              <ItemBorderBox key={projectId} alignItems="stretch" justifyContent="flex-start">
                <VStack align="stretch" spacing={2} width="100%">
                  <Flex align="center" justify="space-between" gap="12px">
                    <Link
                      as={RouterLink}
                      to={`/research/${projectId}`}
                      color={theme.colors.blue800}
                      fontWeight="semibold"
                      textDecoration="underline"
                      _hover={{ textDecoration: 'underline' }}
                    >
                      {projectLabel}
                    </Link>
                    <DeleteOrgAccessIcon
                      organisation={org}
                      objectType={AccessObjectType.PROJECT}
                      objectId={projectId}
                      objectLabel={projectLabel}
                    />
                  </Flex>
                </VStack>
              </ItemBorderBox>
            );
          })
        )}
      </SimpleGrid>
    </>
  );
}

function OrganisationSamplingPointAccess({
  org,
  metadata,
  isMetadataLoading,
  ensureMetadata,
}: {
  org: Organisation;
  metadata: AdminOrganisationAccessMetadataResponse | undefined;
  isMetadataLoading: boolean;
  ensureMetadata: () => void;
}) {
  const samplingPointIds = useMemo(() => sortIds(org.samplingPointAccesses), [org.samplingPointAccesses]);
  const count = samplingPointIds.length;
  const isCollapsible = count > ACCESS_COLLAPSE_THRESHOLD;
  const [collapsed, setCollapsed] = useState(isCollapsible);

  useEffect(() => {
    setCollapsed(isCollapsible);
  }, [isCollapsible]);

  useEffect(() => {
    if (!isCollapsible && count > 0) {
      ensureMetadata();
    }
  }, [isCollapsible, count, ensureMetadata]);

  const projectAccessSet = useMemo(() => new Set(org.projectAccesses), [org.projectAccesses]);

  const samplingPointInfoById = useMemo(() => {
    const map = new Map<number, AdminOrganisationAccessMetadataResponse['samplingPoints'][number]>();
    metadata?.samplingPoints.forEach(sp => {
      map.set(sp.id, sp);
    });
    return map;
  }, [metadata]);

  const handleToggle = useCallback(() => {
    if (collapsed) {
      ensureMetadata();
    }
    setCollapsed(prev => !prev);
  }, [collapsed, ensureMetadata]);

  const showSpinner = isMetadataLoading && !metadata;

  const header = (
    <AccessSectionHeader
      label="Sampling point access"
      count={count}
      collapsible={isCollapsible}
      collapsed={collapsed}
      onToggle={handleToggle}
      isLoading={showSpinner}
    />
  );

  if (count === 0) {
    return (
      <AccessSectionContainer>
        {header}
        <Text mt={3}>-</Text>
      </AccessSectionContainer>
    );
  }

  return (
    <AccessSectionContainer>
      {header}
      {isCollapsible && collapsed ? (
        <Text fontSize="sm" color={theme.colors.neutral600} mt={2}>
          {count} sampling point {count === 1 ? 'access' : 'accesses'} hidden.
        </Text>
      ) : (
        <SimpleGrid minChildWidth="200px" spacingX="20px" spacingY="10px" mt={3}>
          {samplingPointIds.map(samplingPointId => {
            const info = samplingPointInfoById.get(samplingPointId);
            const hasCoverage = info?.projectIds.some(projectId => projectAccessSet.has(projectId)) ?? false;
            const highlight = metadata ? !hasCoverage : false;
            const displayName = info?.name
              ? `${info.name} (#${samplingPointId})`
              : `Sampling point #${samplingPointId}`;
            const highlightStyles = highlight
              ? { borderColor: theme.colors.red500, backgroundColor: theme.colors.red50 }
              : undefined;

            return (
              <ItemBorderBox key={samplingPointId} style={highlightStyles}>
                <Flex align="center" justify="space-between" gap="12px" width="100%">
                  <Box textAlign="left" wordBreak="break-word" flex="1">
                    <Link
                      as={RouterLink}
                      to={`/admin/sampling-point/${samplingPointId}`}
                      display="block"
                      color={theme.colors.neutral800}
                      fontWeight={600}
                      textDecoration="none"
                      _hover={{ textDecoration: 'underline', color: theme.colors.blue800 }}
                    >
                      {displayName}
                    </Link>
                  </Box>
                  <HStack spacing="8px">
                    {highlight ? (
                      <Tooltip label="Organisation cannot reach this sampling point via project access">
                        <Tag size="sm" colorScheme="red" variant="subtle">
                          No project access
                        </Tag>
                      </Tooltip>
                    ) : null}
                    <DeleteOrgAccessIcon
                      organisation={org}
                      objectType={AccessObjectType.SAMPLING_POINT}
                      objectId={samplingPointId}
                    />
                  </HStack>
                </Flex>
              </ItemBorderBox>
            );
          })}
        </SimpleGrid>
      )}
    </AccessSectionContainer>
  );
}

function OrganisationSampleAccess({
  org,
  metadata,
  isMetadataLoading,
  ensureMetadata,
}: {
  org: Organisation;
  metadata: AdminOrganisationAccessMetadataResponse | undefined;
  isMetadataLoading: boolean;
  ensureMetadata: () => void;
}) {
  const sampleIds = useMemo(() => sortIds(org.sampleAccesses), [org.sampleAccesses]);
  const count = sampleIds.length;
  const isCollapsible = count > ACCESS_COLLAPSE_THRESHOLD;
  const [collapsed, setCollapsed] = useState(isCollapsible);

  useEffect(() => {
    setCollapsed(isCollapsible);
  }, [isCollapsible]);

  useEffect(() => {
    if (!isCollapsible && count > 0) {
      ensureMetadata();
    }
  }, [isCollapsible, count, ensureMetadata]);

  const projectAccessSet = useMemo(() => new Set(org.projectAccesses), [org.projectAccesses]);

  const sampleInfoById = useMemo(() => {
    const map = new Map<number, AdminOrganisationAccessMetadataResponse['samples'][number]>();
    metadata?.samples.forEach(sample => {
      map.set(sample.id, sample);
    });
    return map;
  }, [metadata]);

  const samplingPointNameById = useMemo(() => {
    const map = new Map<number, string | null>();
    metadata?.samplingPoints.forEach(sp => {
      map.set(sp.id, sp.name ?? null);
    });
    return map;
  }, [metadata]);

  const handleToggle = useCallback(() => {
    if (collapsed) {
      ensureMetadata();
    }
    setCollapsed(prev => !prev);
  }, [collapsed, ensureMetadata]);

  const showSpinner = isMetadataLoading && !metadata;

  const header = (
    <AccessSectionHeader
      label="Sample access"
      count={count}
      collapsible={isCollapsible}
      collapsed={collapsed}
      onToggle={handleToggle}
      isLoading={showSpinner}
    />
  );

  if (count === 0) {
    return (
      <AccessSectionContainer>
        {header}
        <Text mt={3}>-</Text>
      </AccessSectionContainer>
    );
  }

  return (
    <AccessSectionContainer>
      {header}
      {isCollapsible && collapsed ? (
        <Text fontSize="sm" color={theme.colors.neutral600} mt={2}>
          {count} sample {count === 1 ? 'access' : 'accesses'} hidden.
        </Text>
      ) : (
        <SimpleGrid minChildWidth="200px" spacingX="20px" spacingY="10px" mt={3}>
          {sampleIds.map(sampleId => {
            const info = sampleInfoById.get(sampleId);
            const hasCoverage = info?.projectIds.some(projectId => projectAccessSet.has(projectId)) ?? false;
            const highlight = metadata ? !hasCoverage : false;

            const samplingPointId = info?.samplingPointId ?? null;
            const samplingPointName =
              info?.samplingPointName ??
              (samplingPointId != null ? samplingPointNameById.get(samplingPointId) ?? null : null);
            const samplingPointDisplay =
              samplingPointId == null
                ? 'Sampling point: not set'
                : `Sampling point: ${
                    samplingPointName ? `${samplingPointName} (#${samplingPointId})` : `#${samplingPointId}`
                  }`;

            const sampleNumberLabel = formatSampleLabel(info?.number ?? null, info?.bioRep ?? null);
            const primaryLabel = sampleNumberLabel
              ? `Sample ${sampleNumberLabel} (#${sampleId})`
              : `Sample #${sampleId}`;
            const secondaryLabel = metadata ? samplingPointDisplay : 'Loading sampling point info…';

            const highlightStyles = highlight
              ? { borderColor: theme.colors.red500, backgroundColor: theme.colors.red50 }
              : undefined;

            return (
              <ItemBorderBox key={sampleId} style={highlightStyles}>
                <Flex align="center" justify="space-between" gap="12px" width="100%">
                  <Box textAlign="left" wordBreak="break-word" flex="1">
                    <Link
                      as={RouterLink}
                      to={`/admin/sample/${sampleId}`}
                      display="block"
                      color={theme.colors.neutral800}
                      fontWeight={600}
                      textDecoration="none"
                      _hover={{ textDecoration: 'underline', color: theme.colors.blue800 }}
                    >
                      {primaryLabel}
                      <Text fontSize="sm" color={theme.colors.neutral600}>
                        {secondaryLabel}
                      </Text>
                    </Link>
                  </Box>
                  <HStack spacing="8px">
                    {highlight ? (
                      <Tooltip label="Organisation cannot reach this sample via project access">
                        <Tag size="sm" colorScheme="red" variant="subtle">
                          No project access
                        </Tag>
                      </Tooltip>
                    ) : null}
                    <DeleteOrgAccessIcon organisation={org} objectType={AccessObjectType.SAMPLE} objectId={sampleId} />
                  </HStack>
                </Flex>
              </ItemBorderBox>
            );
          })}
        </SimpleGrid>
      )}
    </AccessSectionContainer>
  );
}

interface AccessSectionHeaderProps {
  label: string;
  count: number;
  collapsible: boolean;
  collapsed: boolean;
  onToggle: () => void;
  isLoading: boolean;
}

function AccessSectionHeader(props: AccessSectionHeaderProps) {
  const { label, count, collapsible, collapsed, onToggle, isLoading } = props;
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!collapsible) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle();
    }
  };

  const indicator = !collapsible ? null : (
    <Text
      key="indicator"
      fontSize="sm"
      color={theme.colors.neutral500}
      fontWeight={600}
      width="12px"
      textAlign="center"
    >
      {collapsed ? '>' : 'v'}
    </Text>
  );

  const trailing: JSX.Element[] = [];
  if (isLoading) {
    trailing.push(<Spinner size="sm" key="spinner" />);
  }

  return (
    <Flex
      align="center"
      justify="space-between"
      py={2}
      borderBottom={`1px solid ${theme.colors.neutral200}`}
      cursor={collapsible ? 'pointer' : 'default'}
      tabIndex={collapsible ? 0 : undefined}
      role={collapsible ? 'button' : undefined}
      onClick={collapsible ? onToggle : undefined}
      onKeyDown={collapsible ? handleKeyDown : undefined}
      aria-expanded={collapsible ? !collapsed : undefined}
      gap="12px"
    >
      <HStack spacing="8px" align="center" flex="1">
        {indicator}
        <Text fontWeight={600} color={theme.colors.neutral800}>
          {count > 0 ? `${label} (${count})` : label}
        </Text>
      </HStack>
      {trailing.length ? <HStack spacing="6px">{trailing}</HStack> : null}
    </Flex>
  );
}

function formatSampleLabel(sampleNumber: number | null, bioRep: string | null): string | null {
  const numberPart = sampleNumber == null ? '' : String(sampleNumber);
  const bioRepPart = bioRep == null ? '' : String(bioRep).trim();
  const combined = `${numberPart}${bioRepPart}`.trim();
  return combined.length ? combined : null;
}

function sortIds(ids: number[]) {
  return clone(ids).sort((a, b) => (a > b ? 1 : -1));
}

const AccessSectionContainer = styled(Box)`
  padding-top: 30px;
  padding-bottom: 15px;
`;

const FieldTitle = styled(Box)`
  font-weight: 501;
  padding-top: 30px;
  padding-bottom: 15px;
`;
